import { Component } from '@theme/component';

/**
 * Replaces the business-day fallback with calendar dates in the shopper's browser.
 * Pages can be cached, so dates must be computed on the client rather than in Liquid.
 *
 * @typedef {object} DeliveryEstimateRefs
 * @property {HTMLElement} [title] - Arrival window text
 * @property {HTMLElement} [ships] - Ship-by text
 *
 * @extends {Component<DeliveryEstimateRefs>}
 */
class DeliveryEstimate extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.render();
  }

  updatedCallback() {
    super.updatedCallback();
    this.render();
  }

  render() {
    const { title, ships } = this.refs;
    const processing = this.#readDays('processingDays');
    const transitMin = this.#readDays('transitMinDays');
    const transitMax = Math.max(transitMin, this.#readDays('transitMaxDays'));

    const shipDate = addBusinessDays(new Date(), processing);
    const start = addBusinessDays(shipDate, transitMin);
    const end = addBusinessDays(shipDate, transitMax);

    const format = this.#formatter();
    const titleTemplate = this.dataset.titleTemplate;
    const shipsTemplate = this.dataset.shipsTemplate;

    if (title && titleTemplate) {
      title.textContent =
        start.toDateString() === end.toDateString()
          ? titleTemplate.replace('[start]', format(start)).replace(/\s*[–-]\s*\[end\]/, '')
          : titleTemplate.replace('[start]', format(start)).replace('[end]', format(end));
    }

    if (ships && shipsTemplate) {
      ships.textContent = shipsTemplate.replace('[date]', format(shipDate));
    }
  }

  /**
   * @param {string} key
   * @returns {number}
   */
  #readDays(key) {
    const value = Number.parseInt(this.dataset[key] ?? '0', 10);
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  /** @returns {(date: Date) => string} */
  #formatter() {
    const locale = this.dataset.locale || document.documentElement.lang || undefined;
    try {
      const formatter = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });
      return (date) => formatter.format(date);
    } catch {
      return (date) => date.toLocaleDateString();
    }
  }
}

/**
 * Adds business days (Monday–Friday) to a date.
 * @param {Date} from
 * @param {number} days
 * @returns {Date}
 */
function addBusinessDays(from, days) {
  const date = new Date(from);
  let remaining = days;
  while (remaining > 0) {
    date.setDate(date.getDate() + 1);
    const weekday = date.getDay();
    if (weekday !== 0 && weekday !== 6) remaining--;
  }
  return date;
}

if (!customElements.get('delivery-estimate')) {
  customElements.define('delivery-estimate', DeliveryEstimate);
}
